"use client";

import React from "react";
import { useNavigate } from "react-router-dom";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { logger } from "@/telemetry";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useIntegrationOAuth } from "@/hooks/useIntegrationOAuth";
import { useWorkflowList } from "@/hooks/chat/useWorkflowList";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";

import { ChatCommandMenu } from "./ChatCommandMenu";
import { useAddMenu, useAddMenuKeyboardNav, useSkillsCatalogue } from "./ChatInputActions.hooks";
import { AddMenu } from "./ChatInputActions.AddMenu";
import {
  ConfigurationControl,
  KnowledgeHubControl,
  PromptoControl,
  PromptsControl,
  SlashAffordance,
  WebSearchControl,
} from "./ChatInputActions.InlineControls";
import {
  ActiveFeatureChips,
  ModelSelectorSlot,
  SendStopButton,
  SpeechError,
  VoiceButton,
} from "./ChatInputActions.RightControls";

export interface ChatInputActionsProps {
  /** Current prompt text — used to determine send-button state. */
  prompt: string;
  /** Whether the AI response is currently streaming. */
  streaming: boolean;
  /** Whether a file upload request is in progress. */
  isUploading: boolean;
  /** Whether uploaded files are still being processed by the backend. */
  isUploadingFiles: boolean;
  /** Whether the prompt input is locked (pre-filled workflow default). */
  isPromptLocked: boolean;
  /** Whether web search is currently enabled. */
  isSearchEnabled: boolean;
  /** Whether Knowledge Hub is currently enabled. */
  isKnowledgeHubEnabled: boolean;
  /** Whether the configuration panel is disabled (specialised workflow or active chat). */
  isConfigurationDisabled: boolean;
  /** Whether voice recording is active. */
  listening: boolean;
  /** Whether the current browser supports the Web Speech API. */
  browserSupportsSpeechRecognition: boolean;
  /** Non-null when there is a speech-recognition error to surface. */
  speechError: string | null;

  // ── Visibility (permission gates — always respected) ────────────────
  showFileUpload: boolean;
  showVoice: boolean;
  showSearch: boolean;
  showKnowledgeHub: boolean;
  showPrompts: boolean;
  showPrompto: boolean;
  showSettings: boolean;
  showChatModelSelector: boolean;

  // ── UI preferences ───────────────────────────────────────────────────
  /** When true, only pinned controls are shown inline; unpinned ones live in the menus. */
  simpleChatInput: boolean;
  /** Per-control pinned state for the chat input toolbar. */
  pinnedChatControls: {
    prompts: boolean;
    prompto: boolean;
    search: boolean;
    knowledgeHub: boolean;
    configuration: boolean;
    modelSelector: boolean;
  };

  // ── Command menu state ───────────────────────────────────────────────
  commandMenuVisible: boolean;
  commandMenuPosition: { top: number; left: number };
  /** Increment to request opening the "+" add menu (e.g. the user typed "+"). */
  openAddMenuSignal?: number;

  // ── Handlers ────────────────────────────────────────────────────────
  onFileUploadClick: () => void;
  onVoicePress: () => void;
  onWebSearchToggle: (val: boolean) => void;
  onKnowledgeHubToggle: () => void;
  onBrowsePrompts: () => void;
  onPrompto: () => void;
  onOpenConfiguration: () => void;
  onCommandMenuOpen: () => void;
  onCommandMenuClose: () => void;
  onStop: () => void;
  /** Called when the user activates the "Mention people or workflows" item in the add menu. */
  onMention: () => void;
  /** Called with the skill's catalogue id when a skill row is chosen from the command menu. */
  onSelectSkill: (skillId: string) => void;

  // ── Refs ─────────────────────────────────────────────────────────────
  fileInputRef: React.RefObject<HTMLInputElement>;

  // ── File input element props ─────────────────────────────────────────
  allowedFileTypes: string[];
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * ChatInputActions
 *
 * Shared action row for the chat composer, rendered at the bottom of the
 * PromptWithActions form. Provides:
 *   - Bottom-left: "+" add menu (attach file / mention), "/" slash affordance,
 *     and any inline controls (pinned or advanced-mode).
 *   - Bottom-right: active-feature chips (gated by showActiveFeatureChips pref),
 *     model selector, voice microphone, send / stop button.
 *   - A hidden file <input> and a ChatCommandMenu portal.
 *
 * The helper-line hint ("Type / for commands…") is intentionally rendered
 * OUTSIDE this component by the parent, below the <form> element.
 */
export const ChatInputActions = ({
  prompt,
  streaming,
  isUploading,
  isUploadingFiles,
  isPromptLocked,
  isSearchEnabled,
  isKnowledgeHubEnabled,
  isConfigurationDisabled,
  listening,
  browserSupportsSpeechRecognition,
  speechError,
  showFileUpload,
  showVoice,
  showSearch,
  showKnowledgeHub,
  showPrompts,
  showPrompto,
  showSettings,
  showChatModelSelector,
  simpleChatInput,
  pinnedChatControls,
  commandMenuVisible,
  commandMenuPosition,
  openAddMenuSignal,
  onFileUploadClick,
  onVoicePress,
  onWebSearchToggle,
  onKnowledgeHubToggle,
  onBrowsePrompts,
  onPrompto,
  onOpenConfiguration,
  onCommandMenuOpen,
  onCommandMenuClose,
  onStop,
  onMention,
  onSelectSkill,
  fileInputRef,
  allowedFileTypes,
  onFileChange,
}: ChatInputActionsProps) => {
  const { brand } = useBrand();
  const navigate = useNavigate();
  const { authenticatedFetch } = useWebuildAPIClient();
  const { showActiveFeatureChips, setPinnedChatControl } = useUIPreferences();
  const { workflow } = useWorkflowStore();
  const { ensureToolConnected, connectingToolId, prefetchStatuses, getToolConnectionStatus } =
    useIntegrationOAuth();
  const { canCreateWorkflows } = useCapabilities();
  // Workflows the user can switch to, surfaced as a section in the command menu.
  const { workflows } = useWorkflowList();

  // Brand-localised heading for the Workflows section (e.g. NGV renders its own
  // label), matching how the sidebar names the Workflows area.
  const workflowsLabel = brand.copy.menuItems?.["Workflows"];

  // Real skills catalogue for the command menu's Skills section, fetched once
  // the command menu first opens.
  const skills = useSkillsCatalogue(commandMenuVisible, authenticatedFetch);

  /**
   * Switch the chat to a different workflow. Reuses the existing
   * /workflows/:id route (the same additive path the Prompto button uses), so
   * there is no bespoke "attach workflow" call. Navigating starts a fresh chat
   * with that workflow; the previous chat is preserved in chat history.
   */
  const handleSelectWorkflow = (workflowId: string) => {
    logger.info("ChatInputActions: workflow selected", { workflowId });
    onCommandMenuClose();
    navigate(`/workflows/${workflowId}`);
  };

  /**
   * "Browse all workflows" footer action: open the Library on its Workflows
   * tab, which replaced the standalone Workflows index (ADR-0019).
   */
  const handleBrowseAllWorkflows = () => {
    logger.info("ChatInputActions: browse all workflows");
    onCommandMenuClose();
    navigate("/library?tab=workflow");
  };

  /**
   * "New workflow" footer action: open the Workflow Builder on a new workflow.
   * Gated on `canCreateWorkflows` from the shared capability layer, so this
   * action, the Library's "+ New" menu and the Library's Clone action all agree.
   * Checking `workflows:create` alone here missed the `workflows:admin` grant.
   */
  const handleCreateWorkflow = () => {
    logger.info("ChatInputActions: create new workflow");
    onCommandMenuClose();
    navigate("/workflow-builder");
  };

  // The "+" add menu's open state and view, held here so they outlive the menu.
  const addMenu = useAddMenu({
    openAddMenuSignal,
    navigate,
    prefetchStatuses,
    ensureToolConnected,
    onFileUploadClick,
    onMention,
  });

  // ArrowUp/Down roving focus across the add menu's action rows while it is open.
  useAddMenuKeyboardNav(addMenu.isOpen);

  /**
   * Determines whether a named control should be rendered inline.
   *
   * Permission gates (`permissionAllows`) are always respected regardless of
   * mode. In advanced mode all permitted controls are shown; in simple mode
   * only pinned controls appear inline (unpinned ones live in the menus).
   */
  const isControlVisible = (
    key: keyof typeof pinnedChatControls,
    permissionAllows: boolean,
  ): boolean => {
    if (!permissionAllows) return false;
    if (!simpleChatInput) return true; // advanced mode: show all

    return pinnedChatControls[key]; // simple mode: only pinned
  };

  return (
    <div
      className="relative z-10 flex w-full flex-col gap-2 px-4 pb-4 shrink-0 bg-default-50 dark:bg-content2 rounded-b-2xl"
      data-id="prompt-actions-container"
    >
      {/* ── Main action row ──────────────────────────────────────────────── */}
      <div className="flex w-full items-center justify-between gap-2">
        {/* LEFT: "+" add menu, "/" slash affordance, then any inline controls */}
        <div className="flex items-center gap-2 flex-wrap">
          <AddMenu
            integrations={{ connectingToolId, getToolConnectionStatus }}
            menu={addMenu}
            show={showFileUpload}
          />

          <SlashAffordance
            commandMenuVisible={commandMenuVisible}
            onCommandMenuOpen={onCommandMenuOpen}
          />

          {/* ── Inline controls (visibility governed by isControlVisible) ── */}
          <WebSearchControl
            isSearchEnabled={isSearchEnabled}
            visible={isControlVisible("search", showSearch)}
            onWebSearchToggle={onWebSearchToggle}
          />
          <KnowledgeHubControl
            isKnowledgeHubEnabled={isKnowledgeHubEnabled}
            visible={isControlVisible("knowledgeHub", showKnowledgeHub)}
            onKnowledgeHubToggle={onKnowledgeHubToggle}
          />
          <PromptsControl
            isPromptLocked={isPromptLocked}
            visible={isControlVisible("prompts", showPrompts)}
            onBrowsePrompts={onBrowsePrompts}
          />
          <PromptoControl
            visible={isControlVisible("prompto", showPrompto)}
            onPrompto={onPrompto}
          />
          <ConfigurationControl
            isConfigurationDisabled={isConfigurationDisabled}
            visible={isControlVisible("configuration", showSettings)}
            onOpenConfiguration={onOpenConfiguration}
          />
        </div>

        {/* RIGHT: active chips (optional), model selector, mic, send ──────── */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          <ActiveFeatureChips
            isKnowledgeHubEnabled={isKnowledgeHubEnabled}
            isSearchEnabled={isSearchEnabled}
            show={showActiveFeatureChips}
            onKnowledgeHubToggle={onKnowledgeHubToggle}
            onWebSearchToggle={onWebSearchToggle}
          />
          <ModelSelectorSlot visible={isControlVisible("modelSelector", showChatModelSelector)} />
          <VoiceButton
            browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
            isPromptLocked={isPromptLocked}
            listening={listening}
            showVoice={showVoice}
            onVoicePress={onVoicePress}
          />
          <SpeechError error={speechError} />
          <SendStopButton
            isUploading={isUploading}
            isUploadingFiles={isUploadingFiles}
            prompt={prompt}
            streaming={streaming}
            onStop={onStop}
          />
        </div>
      </div>

      {/* ── Hidden file input ────────────────────────────────────────────── */}
      <input
        ref={fileInputRef}
        multiple
        accept={allowedFileTypes.join(",")}
        data-id="upload-file-input"
        style={{ display: "none" }}
        type="file"
        onChange={onFileChange}
      />

      {/* ── ChatCommandMenu portal ───────────────────────────────────────── */}
      <ChatCommandMenu
        activeWorkflowId={workflow?.id ?? null}
        isConfigurationDisabled={isConfigurationDisabled}
        isKnowledgeHubEnabled={isKnowledgeHubEnabled}
        isPromptLocked={isPromptLocked}
        isWebSearchEnabled={isSearchEnabled}
        modelSelectorEnabled={pinnedChatControls.modelSelector}
        position={commandMenuPosition}
        showConfiguration={showSettings}
        showKnowledgeHub={showKnowledgeHub}
        showModelSelector={showChatModelSelector}
        showPrompto={showPrompto}
        showPrompts={showPrompts}
        showWebSearch={showSearch}
        showWorkflows={workflows.length > 0}
        skillQuery={prompt.startsWith("/") ? prompt.slice(1) : ""}
        skills={skills}
        visible={commandMenuVisible}
        workflows={workflows}
        workflowsLabel={workflowsLabel}
        onBrowseAllWorkflows={handleBrowseAllWorkflows}
        onBrowsePrompts={onBrowsePrompts}
        onClose={onCommandMenuClose}
        onCreateWorkflow={canCreateWorkflows ? handleCreateWorkflow : undefined}
        onKnowledgeHubToggle={onKnowledgeHubToggle}
        onModelSelectorToggle={(val) => setPinnedChatControl("modelSelector", val)}
        onOpenConfiguration={onOpenConfiguration}
        onPrompto={onPrompto}
        onSelectSkill={onSelectSkill}
        onSelectWorkflow={handleSelectWorkflow}
        onWebSearchToggle={onWebSearchToggle}
      />
    </div>
  );
};
